import { useEffect, useState } from "react";
import { Text } from "@core/index";
import WaterLoader from "@packages/components/water-loader";
import { gradientColors, ThemeColor } from "./gradient-colors";
import { Sparkles, Loader2 } from "lucide-react";

const LoadingDot = ({
  title,
  themeColor,
  fullHeight = true,
}: {
  title: string;
  fullHeight?: boolean;
  themeColor: ThemeColor;
}) => {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState("");

  const selectedGradient = gradientColors[themeColor] || gradientColors["blue"];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let dotsInterval: NodeJS.Timeout;

    // Progress animation
    interval = setInterval(() => {
      setProgress((prev) => {
        // Normal dağılım - ortada hızlı, başta ve sonda yavaş
        if (prev < 15) return prev + 0.8; // Yavaş başlangıç
        if (prev < 30) return prev + 0.2; // Hızlanma başlangıcı
        if (prev < 45) return prev + 0.4; // Hızlı artış
        if (prev < 60) return prev + 0.5; // En hızlı
        if (prev < 75) return prev + 0.4; // Hızlı azalış
        if (prev < 85) return prev + 0.2; // Yavaşlama
        if (prev < 98) return prev + 0.1; // Son damla
        return prev; // 98'de dur
      });
    }, 50);

    // Animated dots
    dotsInterval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...") return "";
        return prev + ".";
      });
    }, 500);

    return () => {
      if (interval) clearInterval(interval);
      if (dotsInterval) clearInterval(dotsInterval);
    };
  }, []);

  return (
    <div
      className={
        fullHeight
          ? `min-h-screen h-full flex items-center justify-center ${selectedGradient.background} relative overflow-hidden`
          : ""
      }
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-white/10 to-transparent rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-white/5 to-transparent rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container max-w-7xl mx-auto px-4 py-4 relative z-10">
        <div className="max-w-4xl mx-auto" data-testid="loading-dot-container">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl px-8 py-12 border border-white/20 dark:border-gray-700/30 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-60"></div>
            <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full opacity-20 animate-ping"></div>
            <div
              className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-tr from-blue-400 to-cyan-500 rounded-full opacity-20 animate-ping"
              style={{ animationDelay: "0.5s" }}
            ></div>

            <div className="flex flex-col items-center justify-center relative z-10">
              {/* Enhanced loader with sparkles */}
              <div className="relative mb-8">
                <WaterLoader progress={progress} />

                {/* Floating sparkles around the loader */}
                <div className="absolute -top-2 -right-2">
                  <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
                </div>
                <div className="absolute -bottom-2 -left-2">
                  <Sparkles
                    className="w-4 h-4 text-blue-400 animate-pulse"
                    style={{ animationDelay: "0.5s" }}
                  />
                </div>
                <div className="absolute top-1/2 -right-6 transform -translate-y-1/2">
                  <Sparkles
                    className="w-3 h-3 text-green-400 animate-pulse"
                    style={{ animationDelay: "1s" }}
                  />
                </div>
                <div className="absolute top-1/2 -left-6 transform -translate-y-1/2">
                  <Sparkles
                    className="w-3 h-3 text-purple-400 animate-pulse"
                    style={{ animationDelay: "1.5s" }}
                  />
                </div>
              </div>

              {/* Enhanced title with animated dots */}
              <div className="text-center space-y-4">
                <Text
                  variant="h2"
                  className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2"
                >
                  {title}
                  <span className="text-amber-500 dark:text-amber-400 ml-2">
                    {dots}
                  </span>
                </Text>

                {/* Progress indicator */}
                <div className="w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>

                {/* Progress percentage */}
                <Text
                  variant="p"
                  className="text-sm text-gray-600 dark:text-gray-400 font-medium"
                >
                  {Math.round(progress)}% tamamlandı
                </Text>
              </div>

              {/* Additional loading indicators */}
              <div className="flex space-x-2 mt-8">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-orange-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-red-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingDot;

export { default as SimpleLoading } from "./SimpleLoading";
